<script setup>
// imports
import {ref, reactive, watchEffect, onMounted} from 'vue'
import {useCartStore} from "../stores/CartStore";
import {modalStore} from "../stores/ModalStore";
import VueMagnifier from '@websitebeaver/vue-magnifier';
import '@websitebeaver/vue-magnifier/styles.css';

const cartStore = useCartStore();
const articleSet = modalStore.articleSet;
const category = modalStore.category;

import api from '../api';
import ToastComponent from "./ToastComponent.vue";

// data
const count = ref(0);
const data = reactive({
    articleSetDetail: null
})

const currentUrl = window.location.pathname
const parts = currentUrl.split("/");
const slug = parts[parts.length - 1];
const isLoading = ref(true);

onMounted(async () => {
    await cartStore.checkLoginStatus();

    if (modalStore.articleSet) {
        const path = `/api/articles/show-article-set-detail/${modalStore.articleSet.slug}`;
        await api.get(path)
            .then(response => {
                if (response.data) {
                    data.articleSetDetail = response.data;
                    const carouselId = '#carousel-article-set-detail';
                    const myCarousel = document.querySelector(carouselId);
                    new bootstrap.Carousel(myCarousel);
                }
            }).catch(function (error) {
                    console.error('Failed to fetch articleset: ', error);
                }
            );
    }
    isLoading.value = false;
})

function addToCart() {
    if (count.value > 0) {
        if (data.articleSetDetail && data.articleSetDetail.articlesetimage) {
            data.articleSetDetail.imagedir = data.articleSetDetail.articlesetimage.imagedir;
            data.articleSetDetail.image = data.articleSetDetail.articlesetimage.image;
        }
        cartStore.addArticleSets(count.value, data.articleSetDetail);
        count.value = 0;
    }
}
</script>
<template>
    <div v-if="!isLoading">
        <ToastComponent>
        </ToastComponent>
        <div aria-live="polite" aria-atomic="true" class="position-relative">
            <div class="toast-container top-0 end-0 p-3">
                <div class="toast align-items-center text-bg-primary border-0" role="alert" aria-live="assertive"
                     aria-atomic="true">
                    <div class="d-flex">
                        <div class="toast-body">
                            Der Artikelsatz wurde in den Warenkorb gelegt.
                        </div>
                        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"
                                aria-label="Close"></button>
                    </div>
                </div>
            </div>
        </div>
        <div class="container">
            <div class="row mt-5 mb-5">
                <div class="col">
                    <h1>{{ category }}</h1>
                </div>
            </div>
            <div class="row mb-5">
                <div class="col-5 offset-1">
                    <div id="carousel-article-set-detail" class="carousel slide" data-bs-theme="dark">
                        <div class="carousel-indicators">
                            <button v-for="(sliderimage, index) in data.articleSetDetail?.slider_set_images"
                                    type="button"
                                    data-bs-target="#carousel-article-set-detail"
                                    :data-bs-slide-to="index"
                                    :class="{ active: index === 0 }"
                                    :aria-current="(index === 0) ? true : null"
                                    :aria-label="'Bild ' + (index + 1)"></button>
                        </div>
                        <div class="carousel-inner">
                            <div v-for="(sliderimage, index) in data.articleSetDetail?.slider_set_images"
                                 class="carousel-item"
                                 :class="{ active: index === 0 }">
                                <img
                                    :src="'/images/' + sliderimage.imagedir + sliderimage.image + '?h=450'"
                                    class="img-fluid">
                            </div>
                        </div>
                        <button class="carousel-control-prev" type="button" data-bs-target="#carousel"
                                data-bs-slide="prev">
                            <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span class="visually-hidden">Zurück</span>
                        </button>
                        <button class="carousel-control-next" type="button" data-bs-target="#carousel"
                                data-bs-slide="next">
                            <span class="carousel-control-next-icon" aria-hidden="true"></span>
                            <span class="visually-hidden">Vor</span>
                        </button>
                        <div v-if="data.articleSetDetail?.badge" class="article-badge">
                            <img v-if="data.articleSetDetail?.badge === 'new'" :src="'/img/new_product.svg'"
                                 alt="NEW Product"
                                 class="img-fluid"/>
                            <img v-else-if="data.articleSetDetail?.badge === 'top'" :src="'/img/top_seller.svg'"
                                 alt="TOP Seller"
                                 class="img-fluid"/>
                        </div>
                    </div>
                </div>
                <div class="col-6 mt-5">
                    <p>
                        <span class="small">ARTIKELNUMMER</span><br>
                        <strong>{{ data.articleSetDetail?.articlesetnumber }}</strong>
                    </p>
                    <h2>{{ data.articleSetDetail?.name }}</h2>
                    <div v-html="data.articleSetDetail?.description"/>
                    <p v-if="data.articleSetDetail?.discount > 0" class="discount-red">{{
                            (1 * data.articleSetDetail?.rentalprice_discounted).toLocaleString('de-CH', {
                                style: 'currency',
                                currency: 'CHF'
                            })
                        }} / {{ Number(data.articleSetDetail?.discount).toFixed(2) }}%<br>
                        <s>{{
                                (1 * data.articleSetDetail?.price).toLocaleString('de-CH', {
                                    style: 'currency',
                                    currency: 'CHF'
                                })
                            }}</s></p>
                    <p v-else>
                        <strong>{{
                                (1 * data.articleSetDetail?.price).toLocaleString('de-CH', {
                                    style: 'currency',
                                    currency: 'CHF'
                                })
                            }}</strong>
                    </p>
                    <div class="mb-3">
                        <input v-model="count" type="number" min="0" v-on:keyup.enter="addToCart" class="form-control"/>
                        <button class="btn btn-success ms-2" role="button"
                                aria-label="Warenkorb" title="zum Warenkorb hinzufügen"
                                @click="addToCart">
                            in den Warenkorb
                        </button>
                    </div>
                    <div class="mb-3">
                        <a v-if="cartStore.role === 'admin'"
                           :href="'/admin/articlesets/view/' + data.articleSetDetail?.id">Link
                            zum
                            Artikel</a>
                        <a v-else-if="slug !== data.articleSetDetail?.slug"
                           :href="'/articlesets/' + data.articleSetDetail?.slug">Link zum
                            Artikelsatz</a>
                    </div>
                </div>
            </div>
        </div>
        <section class="mb-5">
            <div class="accordion" id="articleSetDetailAccordion">
                <div class="accordion-item">
                    <h2 class="accordion-header" id="headingOne-articleSetDetail">
                        <button
                            class="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseOne-articleSetDetail"
                            aria-expanded="true"
                            aria-labelledby="headingOne-articleSetDetail"
                            aria-controls="collapseOne-articleSetDetail"
                        >
                            Artikel
                        </button>
                    </h2>
                    <div
                        id="collapseOne-articleSetDetail"
                        class="accordion-collapse collapse show"
                        data-bs-parent="#articleSetDetailAccordion"
                    >
                        <div class="accordion-body">
                            <table class="table">
                                <thead>
                                <tr>
                                    <th class="w-250px">
                                        Artikel
                                    </th>
                                    <th></th>
                                    <th
                                        class="w-250px text-center"
                                    >
                                        Anzahl
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="product in data.articleSetDetail?.articles" :key="product.id">
                                    <td class="text-center">
                                        <a :href="'/articles/' + product.slug"><img
                                            :src="
                    '/images/' +
                    product.articleimage.imagedir +
                    product.articleimage.image +
                    '?w=100&h=100&fit=crop'
                "
                                            class="img-fluid"
                                        />
                                        </a>
                                    </td>
                                    <td class="text-start text-uppercase">{{ product.name }}</td>
                                    <td class="text-center">
                                        {{ product._joinData.quantity }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
<style scoped>
input {
    width: 4rem;
    text-align: center;
    display: inline-block;
}

.carousel-indicators button {
    width: .8rem;
    height: .8rem;
    border-radius: 100%;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
}
</style>
